import { extractAppCommand } from './CommandRegistry.js';
import { mayActivatePtt, pttVisibleStatus } from './PttPolicy.js';
const ACTIVE_STATUSES = new Set([
    'connecting',
    'ready',
    'speaking',
    'listening',
]);
let fallbackId = 0;
export class RealtimeAgentCore {
    ports;
    listeners = new Set();
    status = 'idle';
    transcript = [];
    activeTool = null;
    isMicActive = false;
    context = null;
    errorMessage = null;
    opening = false;
    snapshotCache = null;
    constructor(ports = {}) {
        this.ports = ports;
    }
    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };
    getSnapshot = () => {
        if (!this.snapshotCache) {
            this.snapshotCache = Object.freeze({
                status: this.status,
                transcript: Object.freeze(this.transcript.map((entry) => Object.freeze({ ...entry }))),
                activeTool: this.activeTool,
                isMicActive: this.isMicActive,
                context: this.context,
                errorMessage: this.errorMessage,
            });
        }
        return this.snapshotCache;
    };
    get isActive() {
        return ACTIVE_STATUSES.has(this.status);
    }
    beginOpen(context = null) {
        if (this.opening || this.isActive)
            return false;
        this.opening = true;
        this.context = context;
        this.errorMessage = null;
        this.transition('connecting');
        this.ports.audioOwnership?.setRealtimeOwned(true);
        this.ports.telemetry?.info('realtime.open.started');
        return true;
    }
    markReady() {
        if (!this.opening && !this.isActive)
            return;
        this.opening = false;
        this.isMicActive = false;
        this.transition('ready');
        this.ports.telemetry?.info('realtime.open.ready');
    }
    markSpeaking() {
        if (!this.isActive)
            return;
        this.isMicActive = false;
        this.transition('speaking');
    }
    failOpen(error, message = 'Realtime session could not be opened') {
        this.opening = false;
        this.isMicActive = false;
        this.activeTool = null;
        this.errorMessage = message;
        this.transition('error');
        this.ports.audioOwnership?.setRealtimeOwned(false);
        this.ports.telemetry?.error('realtime.open.failed', error);
    }
    close() {
        const wasOpen = this.opening || this.isActive;
        this.opening = false;
        this.isMicActive = false;
        this.activeTool = null;
        this.context = null;
        this.transition('closed');
        this.ports.audioOwnership?.setRealtimeOwned(false);
        if (wasOpen)
            this.ports.telemetry?.info('realtime.closed');
    }
    setMicActive(active) {
        if (active && !mayActivatePtt(this.status))
            return false;
        if (!active && !this.isActive)
            return false;
        this.isMicActive = active;
        this.transition(pttVisibleStatus(active));
        this.ports.telemetry?.info('realtime.ptt.changed', { active });
        return true;
    }
    async runToolCall(request, execute) {
        if (!this.isActive) {
            throw new Error('Cannot run a Realtime tool without an active session');
        }
        this.activeTool = request.name;
        this.invalidate();
        this.ports.telemetry?.info('realtime.tool.started', { name: request.name });
        try {
            const rawResult = await execute(request);
            const { command, cleanResult } = extractAppCommand(rawResult);
            const commandHandled = command && this.ports.registry
                ? await this.ports.registry.dispatch(command)
                : false;
            this.ports.telemetry?.info('realtime.tool.completed', {
                name: request.name,
                command: command?.name,
                commandHandled,
            });
            return { cleanResult, command, commandHandled };
        }
        catch (error) {
            this.ports.telemetry?.error('realtime.tool.failed', error, { name: request.name });
            throw error;
        }
        finally {
            this.activeTool = null;
            this.invalidate();
        }
    }
    appendTranscript(entry) {
        const normalized = {
            id: entry.id ?? this.nextId(),
            speaker: entry.speaker,
            text: entry.text,
            ts: entry.ts ?? this.now(),
            ...(entry.partial === undefined ? {} : { partial: entry.partial }),
        };
        this.transcript = [...this.transcript, normalized];
        this.persistHistory();
        this.invalidate();
        return normalized;
    }
    upsertTranscript(entry) {
        const index = this.transcript.findIndex((candidate) => candidate.id === entry.id);
        this.transcript = index === -1
            ? [...this.transcript, { ...entry }]
            : this.transcript.map((candidate, candidateIndex) => (candidateIndex === index ? { ...entry } : candidate));
        this.persistHistory();
        this.invalidate();
    }
    removeTranscript(id) {
        const next = this.transcript.filter((entry) => entry.id !== id);
        if (next.length === this.transcript.length)
            return;
        this.transcript = next;
        this.persistHistory();
        this.invalidate();
    }
    clearTranscript() {
        this.transcript = [];
        this.ports.history?.clear();
        this.invalidate();
    }
    restoreHistory() {
        const history = this.ports.history?.load();
        if (!history)
            return false;
        this.transcript = history.transcript.map((entry) => ({ ...entry }));
        this.context = history.context;
        this.invalidate();
        return true;
    }
    transition(nextStatus) {
        if (this.status === nextStatus)
            return;
        this.status = nextStatus;
        this.invalidate();
    }
    persistHistory() {
        this.ports.history?.save({
            transcript: this.transcript.map((entry) => ({ ...entry })),
            context: this.context,
        });
    }
    invalidate() {
        this.snapshotCache = null;
        for (const listener of this.listeners)
            listener();
    }
    now() {
        return this.ports.now?.() ?? Date.now();
    }
    nextId() {
        return this.ports.nextId?.() ?? `realtime-${++fallbackId}`;
    }
}
//# sourceMappingURL=RealtimeAgentCore.js.map